import { DotsThreeVerticalIcon } from '@phosphor-icons/react';
import { css, html } from 'react-strict-dom';

import {
  DropdownMenu,
  DropdownMenuItem,
  Stack,
  Text,
} from '../../../components';
import { Color, Style } from '../../../constants';
import { color } from '../../../constants/color.css';
import TicketBadge from '../../client-tickets/components/TicketBadge';
import {
  type Idea,
  type MoveDestination,
  type ServiceOrderGroup,
  serviceOrderStatusLabel,
} from '../board';

type Props = {
  order: ServiceOrderGroup;
  ideas: ReadonlyArray<Idea>;
  hrefForIdea: (idea: Idea) => string;
  onOpenIdea: (idea: Idea) => void;
  canManage: boolean;
  onUnlink: ((idea: Idea, state: MoveDestination) => void) | null;
};

export default function ServiceOrderCard({
  order,
  ideas,
  hrefForIdea,
  onOpenIdea,
  canManage,
  onUnlink,
}: Props) {
  return (
    <html.div style={styles.card}>
      <Stack gap={0.75}>
        <html.div style={[Style.row, Style.alignCenter, styles.topRow]}>
          <Text typeface="caption.semiBold" color="neutral60">
            #{order.referenceNumber ? `SO-${order.referenceNumber}` : 'SO'}
          </Text>
          <TicketBadge
            label={serviceOrderStatusLabel(order.status)}
            bgColor={Color.blueGreyBg}
            textColor={Color.blueGrey}
          />
        </html.div>
        <Text typeface="body.medium" color="neutral100">
          {order.name}
        </Text>
      </Stack>

      <html.div style={styles.divider} />

      <Stack gap={0.5}>
        <Text typeface="caption.semiBold" color="neutral60">
          Ideas ({ideas.length})
        </Text>
        {ideas.map((idea) => (
          <html.div key={idea.id} style={styles.nestedRow}>
            <html.a
              href={hrefForIdea(idea)}
              style={styles.nestedIdea}
              onClick={(e) => {
                e.preventDefault();
                onOpenIdea(idea);
              }}
            >
              <Text typeface="body.small.medium" color="neutral80">
                {idea.title}
              </Text>
            </html.a>
            {canManage && onUnlink && (
              <DropdownMenu
                anchorSide="right"
                trigger={
                  <html.button
                    style={styles.unlinkButton}
                    aria-label="Unlink from service order"
                  >
                    <DotsThreeVerticalIcon size={18} color={Color.neutral60} />
                  </html.button>
                }
              >
                {(close: () => void) => (
                  <>
                    <DropdownMenuItem
                      showRadio={false}
                      onClick={() => {
                        close();
                        onUnlink(idea, 'idea');
                      }}
                    >
                      Unlink to Ideas
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      showRadio={false}
                      onClick={() => {
                        close();
                        onUnlink(idea, 'preparing');
                      }}
                    >
                      Unlink to Preparing
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenu>
            )}
          </html.div>
        ))}
      </Stack>
    </html.div>
  );
}

const styles = css.create({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 16,
    backgroundColor: color.neutral0,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 12,
  },
  topRow: {
    gap: 8,
    justifyContent: 'space-between',
  },
  divider: {
    height: 1,
    backgroundColor: color.neutral20,
  },
  nestedRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  nestedIdea: {
    flex: 1,
    minWidth: 0,
    padding: 8,
    backgroundColor: color.neutral5,
    borderRadius: 8,
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
    cursor: 'pointer',
    color: 'inherit',
    textDecoration: 'none',
  },
  unlinkButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    padding: 0,
    flexShrink: 0,
    backgroundColor: color.neutral5,
    borderWidth: 0,
    borderRadius: 8,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: color.neutral10,
    },
  },
});
